<template>
  <div class="indicador-input" :class="{ 'indicador-input--sm': size === 'sm' }" ref="wrapper">
    <input
      type="text"
      class="indicador-input__field"
      :value="value"
      :placeholder="placeholder"
      :maxlength="32"
      @input="onInput"
      @keyup.enter="$emit('enter')"
    />
    <button
      type="button"
      class="indicador-input__trigger"
      :class="{ 'indicador-input__trigger--active': pickerOpen }"
      title="Escolher um emoji"
      @click="togglePicker"
    >
      <span class="indicador-input__trigger-face">🙂</span>
      <fa-icon icon="chevron-down" class="indicador-input__chevron" />
    </button>

    <div v-if="pickerOpen" class="indicador-input__popup">
      <Picker
        :data="emojiIndex"
        :native="true"
        :show-preview="false"
        :show-skin-tones="false"
        :auto-focus="true"
        :emoji-size="20"
        :per-line="8"
        :i18n="i18n"
        title="Escolha um emoji"
        @select="onSelectEmoji"
      />
    </div>
  </div>
</template>

<script>
import { Picker, EmojiIndex } from "emoji-mart-vue-fast";
import emojiData from "emoji-mart-vue-fast/data/all.json";
import "emoji-mart-vue-fast/css/emoji-mart.css";
import { normalizarIndicador } from "@/helpers/grapheme";

// EmojiIndex é pesado para construir; compartilhamos uma única instância entre
// todos os usos do componente (criada sob demanda, no primeiro uso).
let sharedEmojiIndex = null;
function getEmojiIndex() {
  if (!sharedEmojiIndex) sharedEmojiIndex = new EmojiIndex(emojiData);
  return sharedEmojiIndex;
}

const I18N_PT_BR = {
  search: "Buscar",
  notfound: "Nenhum emoji encontrado",
  categories: {
    search: "Resultados da busca",
    recent: "Recentes",
    smileys: "Carinhas e emoções",
    people: "Pessoas e corpo",
    nature: "Animais e natureza",
    foods: "Comidas e bebidas",
    activity: "Atividades",
    places: "Viagens e lugares",
    objects: "Objetos",
    symbols: "Símbolos",
    flags: "Bandeiras",
    custom: "Personalizados",
  },
};

export default {
  name: "IndicadorInput",
  components: { Picker },
  props: {
    value: { type: String, default: "" },
    placeholder: { type: String, default: "A" },
    // 'md' (padrão, form de adicionar) ou 'sm' (edição inline da linha)
    size: { type: String, default: "md" },
  },
  data() {
    return {
      pickerOpen: false,
      emojiIndex: getEmojiIndex(),
      i18n: I18N_PT_BR,
    };
  },
  methods: {
    onInput(e) {
      const normalizado = normalizarIndicador(e.target.value);
      // Força o DOM a refletir o valor normalizado mesmo quando o valor emitido
      // não muda a prop (ex.: digitar um 2º caractere que é descartado).
      if (e.target.value !== normalizado) {
        e.target.value = normalizado;
      }
      this.$emit("input", normalizado);
    },
    onSelectEmoji(emoji) {
      this.$emit("input", normalizarIndicador(emoji.native));
      this.pickerOpen = false;
    },
    togglePicker() {
      this.pickerOpen = !this.pickerOpen;
    },
    onDocumentClick(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.pickerOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocumentClick);
  },
};
</script>

<style scoped>
.indicador-input {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 4px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.indicador-input__field {
  flex: 1;
  min-width: 0;
  height: 35px;
  font-size: 13px;
  border-radius: 4px;
  border: 1px solid #d8dbe0;
  padding: 0 6px;
  background-color: #fff;
  text-align: center;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "SFMono-Regular", Menlo, Consolas, monospace;
  font-weight: 700;
  line-height: 1.5;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.indicador-input__field:focus {
  border-color: #3a3e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.indicador-input__trigger {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 35px;
  padding: 0 6px;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
}

.indicador-input__trigger:hover {
  border-color: #3a3e81;
  background: #f2f2f8;
}

.indicador-input__trigger--active {
  border-color: #3a3e81;
  background: #eceaf3;
}

.indicador-input__trigger-face {
  font-size: 15px;
  line-height: 1;
}

.indicador-input__chevron {
  font-size: 9px;
  color: #98a2b3;
}

/* Variante compacta usada na edição inline da linha (alinhar com inputs de 30px) */
.indicador-input--sm .indicador-input__field,
.indicador-input--sm .indicador-input__trigger {
  height: 30px;
  font-size: 12px;
}

.indicador-input__popup {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
}

/* O emoji-mart renderiza seu próprio tema; apenas garantimos a sombra/borda do popover */
.indicador-input__popup >>> .emoji-mart {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}
</style>
